import type { TargetClip } from '@/features/onboarding/components/spotlight'
import type { Rect } from '@/features/onboarding/lib/welcome-tour'

// DOM lookups the tours share. Kept out of spotlight.tsx so that file exports only
// components and hooks, which Fast Refresh can hot-swap without a full reload.

/** The first match for `selector` under `root` that has a size and is on screen. */
export function firstVisible(root: ParentNode, selector: string): HTMLElement | undefined {
  return Array.from(root.querySelectorAll<HTMLElement>(selector)).find(isOnScreen)
}

export function isOnScreen(node: HTMLElement): boolean {
  const r = node.getBoundingClientRect()
  return (
    r.width > 0 &&
    r.height > 0 &&
    r.bottom > 0 &&
    r.top < window.innerHeight &&
    // A kept-mounted but `invisible` tab still has a box.
    node.checkVisibility({ visibilityProperty: true })
  )
}

/** Where a tour's target stops looking for scroll areas: its widget or its page. */
const SCOPE_ROOT = '[data-widget-id], [data-tutorial-page]'

/**
 * The scroll areas between a target and its widget (or page), innermost first, the widget
 * or page root included. Above that — the dashboard's own scrolling — isn't the widget's
 * business: a whole widget below the fold is driver's to scroll into view.
 */
export function scrollAncestors(el: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = []
  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node)
    if (overflowY !== 'visible') out.push(node)
    if (node.matches(SCOPE_ROOT)) break
  }
  return out
}

/**
 * How much of a target the scroll areas around it let show, when one cuts it off (vertically;
 * the tours' widgets scroll that way). Undefined when it's fully in view.
 */
export function measureClip(rect: Rect, scrollers: HTMLElement[]): TargetClip | undefined {
  let top = rect.top
  let bottom = rect.top + rect.height
  let container: HTMLElement | null = null
  let box: DOMRect | null = null
  for (const scroller of scrollers) {
    const b = scroller.getBoundingClientRect()
    if (!container && (rect.top < b.top - 1 || rect.top + rect.height > b.bottom + 1)) {
      container = scroller
      box = b
    }
    top = Math.max(top, b.top)
    bottom = Math.min(bottom, b.bottom)
  }
  if (!container || !box) return undefined
  top = Math.max(top, 0)
  bottom = Math.min(bottom, window.innerHeight)
  const visible = Math.max(0, bottom - top)
  // A target taller than its scroll area counts as fully shown once it fills it.
  const shown = Math.min(1, visible / Math.max(1, Math.min(rect.height, box.height)))
  const boxTop = Math.max(box.top, 0)
  const boxBottom = Math.min(box.bottom, window.innerHeight)
  return {
    shown,
    visibleRect: { left: rect.left, top, width: rect.width, height: visible },
    container,
    containerRect: {
      left: box.left,
      top: boxTop,
      width: box.width,
      height: Math.max(0, boxBottom - boxTop),
    },
    direction: rect.top + rect.height / 2 > box.top + box.height / 2 ? 'down' : 'up',
  }
}
